import { Button, Drawer, Tag } from "antd";
import { SwapOutlined } from "@ant-design/icons";

import { EcgOverlayViewer } from "./EcgWaveformViewer/EcgOverlayViewer";
import type { CompareStudy } from "./EcgWaveformViewer/EcgOverlayViewer";
import { EcgWaveformViewer } from "./EcgWaveformViewer";

const DRAWER_WIDTH = "85%";
const DEFAULT_MODALITY = "ECG";
const MAX_COMPARE_STUDIES = 6;

export interface WaveformDrawerStudyMeta {
  subject?: string | null;
  visit_type?: string | null;
  study_date?: string | null;
  modality?: string | null;
}

export interface WaveformDrawerProps {
  open: boolean;
  onClose: () => void;
  imagingStudyId: string | null;
  studyMeta?: WaveformDrawerStudyMeta;
  /**
   * When false, the drawer is the single-record viewer only: no "Compare
   * subjects" control and no overlay/compare mode. Defaults to true so existing
   * callers (Imaging Studies) keep the compare feature. The ECG screen opts out
   * — its multi-subject comparison was intentionally removed.
   */
  showCompare?: boolean;
  /** Compare-mode state — lifted to the parent so the table's onRow can react. */
  compareMode?: boolean;
  onToggleCompareMode?: (enabled: boolean) => void;
  compareStudies?: CompareStudy[];
  onRemoveCompareStudy?: (imagingStudyId: string) => void;
}

const buildTitle = (studyMeta?: WaveformDrawerStudyMeta) => {
  const modality = studyMeta?.modality || DEFAULT_MODALITY;
  const parts = [
    studyMeta?.subject ? `subject ${studyMeta.subject}` : null,
    studyMeta?.visit_type,
    studyMeta?.study_date,
  ].filter(Boolean);
  return parts.length ? `${modality} — ${parts.join(" · ")}` : modality;
};

export const WaveformDrawer = ({
  open,
  onClose,
  imagingStudyId,
  studyMeta,
  showCompare = true,
  compareMode = false,
  onToggleCompareMode,
  compareStudies = [],
  onRemoveCompareStudy,
}: WaveformDrawerProps) => {
  const compareActive = showCompare && compareMode;

  const drawerExtra = showCompare ? (
    <Button
      size="small"
      type={compareActive ? "primary" : "default"}
      icon={<SwapOutlined />}
      onClick={() => onToggleCompareMode?.(!compareActive)}
    >
      Compare subjects
    </Button>
  ) : undefined;

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (compareActive) onToggleCompareMode?.(false);
        onClose();
      }}
      width={DRAWER_WIDTH}
      destroyOnHidden
      title={compareActive ? "ECG — Compare subjects" : buildTitle(studyMeta)}
      extra={drawerExtra}
    >
      {open && imagingStudyId && (
        <>
          {compareActive ? (
            <>
              {/* Active-study chips with remove buttons */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs text-gray-500 mr-1">
                  {compareStudies.length === 0
                    ? "Click rows in the table to add subjects."
                    : `Comparing (${compareStudies.length}/${MAX_COMPARE_STUDIES})${compareStudies.length < MAX_COMPARE_STUDIES ? " — click rows to add" : ""}:`}
                </span>
                {compareStudies.map((study) => (
                  <Tag
                    key={study.imagingStudyId}
                    closable
                    onClose={() => onRemoveCompareStudy?.(study.imagingStudyId)}
                    color={study.color}
                    className="text-xs"
                  >
                    {study.label}
                  </Tag>
                ))}
              </div>
              {compareStudies.length > 0 && (
                <EcgOverlayViewer studies={compareStudies} />
              )}
            </>
          ) : (
            <EcgWaveformViewer imagingStudyId={imagingStudyId} />
          )}
        </>
      )}
    </Drawer>
  );
};
